import { Input, PasswordInput, PhoneInput } from '@/components/forms';
import LoadingSpinner from '@/components/LoadingSpinner';
import Logo from '@/components/Logo';
import { invitationService } from '@/services/invitationService';
import { OnboardingUserData, ProjectInvitation } from '@/types';
import { getCountryCodes } from '@/utils/phoneValidation';
import { AlertCircle, Building, Check } from 'lucide-react';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';

const InvitationAcceptance: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);

  const [invitation, setInvitation] = useState<ProjectInvitation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
    phone: '',
    countryCode: '+1',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const countryCodes = getCountryCodes();

  // Helper function to safely extract project ID as string
  const getProjectIdString = (projectId: unknown): string => {
    if (typeof projectId === 'string') return projectId;
    if (typeof projectId === 'object' && projectId) {
      return (
        (projectId as { _id?: string; toString?: () => string })._id ||
        (projectId as { toString: () => string }).toString()
      );
    }
    return '';
  };

  // Load invitation details
  useEffect(() => {
    const loadInvitation = async () => {
      if (!token) {
        setError('Invalid invitation link');
        setIsLoading(false);
        return;
      }

      try {
        const response = await invitationService.getInvitation(token);
        if (response.success && response.data) {
          setInvitation(response.data);

          // Pre-fill form with any available data from invitation metadata
          if (response.data?.metadata?.firstName && response.data?.metadata?.lastName) {
            setFormData(prev => ({
              ...prev,
              firstName: response.data?.metadata?.firstName || '',
              lastName: response.data?.metadata?.lastName || '',
            }));
          }

          // If the invitation has user data (for existing users), prefill form
          if (response.data.metadata?.userExists) {
            // Existing user - redirect to login or auto-accept
            // For now, we'll show a different UI for existing users
          }
        } else {
          setError(response.message || 'Invitation not found or expired');
        }
      } catch (error) {
        console.error('Error loading invitation:', error);
        setError('Failed to load invitation details');
      } finally {
        setIsLoading(false);
      }
    };

    loadInvitation();
  }, [token]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must contain uppercase, lowercase, and number';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !invitation || !token) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const onboardingData: OnboardingUserData = {
        invitationToken: token,
        firstName: formData.firstName,
        lastName: formData.lastName,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        phone: formData.phone || undefined,
        countryCode: formData.countryCode,
        acceptTerms: true,
      };

      const response = await invitationService.completeOnboarding(onboardingData);

      if (response.success) {
        setSuccess(true);
        // Redirect to login after a delay with return URL to project
        setTimeout(() => {
          navigate('/login', {
            state: {
              message: 'Account created successfully! Please log in to access your project.',
              email: invitation.email,
              returnTo: `/dashboard/projects/${getProjectIdString(invitation.projectId)}`,
            },
          });
        }, 2000);
      } else {
        setError(response.message || 'Failed to create account');
      }
    } catch (error) {
      console.error('Error creating account:', error);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExistingUserAccept = async () => {
    if (!invitation || !token) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await invitationService.respondToInvitation({
        invitationId: invitation._id || invitation.id,
        action: 'accept',
      });

      if (response.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate(`/dashboard/projects/${getProjectIdString(invitation.projectId)}`);
        }, 2000);
      } else {
        setError(response.message || 'Failed to accept invitation');
      }
    } catch (error) {
      console.error('Error accepting invitation:', error);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-4 text-gray-600">Loading invitation details...</p>
        </div>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-auto">
          <div className="text-center mb-8">
            <Logo className="mx-auto h-12 w-12" />
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Invalid Invitation</h2>
          </div>

          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="text-center">
              <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Invitation Not Found</h3>
              <p className="text-sm text-gray-600 mb-6">{error}</p>
              <button
                onClick={() => navigate('/login')}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-500 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-400"
              >
                Go to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-auto">
          <div className="text-center mb-8">
            <Logo className="mx-auto h-12 w-12" />
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Success!</h2>
          </div>

          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="text-center">
              <Check className="mx-auto h-12 w-12 text-green-500 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {invitation?.metadata?.userExists ? 'Invitation Accepted' : 'Account Created'}
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                {invitation?.metadata?.userExists
                  ? 'You have successfully joined the project team. Redirecting to dashboard...'
                  : 'Your account has been created successfully. Redirecting to login...'}
              </p>
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-500 mx-auto"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Existing user flow (only if user exists AND is logged in)
  // If user exists but not logged in, they should login first
  if (invitation?.metadata?.userExists) {
    const isLoggedIn = authContext?.user;

    if (!isLoggedIn) {
      // Redirect to login for existing users
      navigate('/login', {
        state: {
          returnTo: `/invitation/accept/${token}`,
          message: 'Please login to accept your project invitation.',
        },
      });
      return null;
    }
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-auto">
          <div className="text-center mb-8">
            <Logo className="mx-auto h-12 w-12" />
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Project Invitation</h2>
          </div>

          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building className="h-8 w-8 text-brand-primary" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">You're invited to join</h3>
              <h4 className="text-xl font-bold text-red-500 mb-4">{invitation.projectName}</h4>
            </div>

            <div className="space-y-4 mb-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Invited by:</span>
                  <span className="font-medium">{invitation.invitedByName || 'Project Team'}</span>
                </div>
                <div className="flex items-center justify-between text-sm mt-2">
                  <span className="text-gray-600">Role:</span>
                  <span className="font-medium">
                    {invitation.role
                      ? invitation.role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                      : 'Team Member'}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm mt-2">
                  <span className="text-gray-600">Email:</span>
                  <span className="font-medium">{invitation.email}</span>
                </div>
              </div>

              {invitation.message && (
                <div className="bg-brand-50 p-4 rounded-lg border border-brand-200">
                  <p className="text-sm text-brand-900 italic">"{invitation.message}"</p>
                </div>
              )}
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <AlertCircle className="h-4 w-4 text-red-400 mr-2" />
                  <span className="text-sm text-red-800">{error}</span>
                </div>
              </div>
            )}

            <button
              onClick={handleExistingUserAccept}
              disabled={isSubmitting}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-500 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-400 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Accepting...
                </div>
              ) : (
                'Accept Invitation'
              )}
            </button>

            <div className="mt-4 text-center">
              <p className="text-xs text-gray-500">
                By accepting, you agree to join this project team with the specified role and
                permissions.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // New user signup flow
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <Logo className="mx-auto h-12 w-12" />
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Join the Team</h2>
          <p className="mt-2 text-sm text-gray-600">
            Complete your account setup to join <strong>{invitation?.projectName}</strong>
          </p>
        </div>

        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {/* Invitation Details */}
          <div className="mb-6 p-4 bg-brand-50 rounded-lg border border-brand-200">
            <div className="text-center">
              <Building className="h-8 w-8 text-brand-primary mx-auto mb-2" />
              <h3 className="text-sm font-medium text-brand-900 mb-1">Project Invitation</h3>
              <p className="text-lg font-bold text-brand-900">{invitation?.projectName}</p>
              <p className="text-sm text-brand-700 mt-1">
                Invited by {invitation?.invitedByName} as {invitation?.role.replace('_', ' ')}
              </p>
            </div>

            {invitation?.message && (
              <div className="mt-3 pt-3 border-t border-brand-200">
                <p className="text-sm text-brand-800 italic">"{invitation.message}"</p>
              </div>
            )}
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                  First Name
                </label>
                <Input
                  id="firstName"
                  type="text"
                  placeholder="Enter your first name"
                  value={formData.firstName}
                  onChange={e => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                  error={errors.firstName}
                />
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                  Last Name
                </label>
                <Input
                  id="lastName"
                  type="text"
                  placeholder="Enter your last name"
                  value={formData.lastName}
                  onChange={e => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                  error={errors.lastName}
                />
              </div>
            </div>

            {/* Email (Read-only) */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email Address
              </label>
              <Input
                id="email"
                type="email"
                value={invitation?.email || ''}
                disabled
                className="bg-gray-50 cursor-not-allowed"
              />
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <PasswordInput
                id="password"
                placeholder="Enter your password"
                value={formData.password}
                onChange={e => setFormData(prev => ({ ...prev, password: e.target.value }))}
                error={errors.password}
              />
              <p className="mt-1 text-xs text-gray-500">
                Must be at least 8 characters with uppercase, lowercase, and number
              </p>
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm Password
              </label>
              <PasswordInput
                id="confirmPassword"
                placeholder="Confirm your password"
                value={formData.confirmPassword}
                onChange={e => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                error={errors.confirmPassword}
              />
            </div>

            {/* Phone (Optional) */}
            <div>
              <PhoneInput
                label="Phone Number (Optional)"
                countryCodes={countryCodes}
                selectedCountryCode={formData.countryCode}
                value={formData.phone}
                onCountryCodeChange={code => setFormData(prev => ({ ...prev, countryCode: code }))}
                onChange={value => setFormData(prev => ({ ...prev, phone: value }))}
                placeholder="Enter your phone number"
              />
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <AlertCircle className="h-4 w-4 text-red-400 mr-2" />
                  <span className="text-sm text-red-800">{error}</span>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-500 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-400 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating Account...
                </div>
              ) : (
                'Create Account & Join Project'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              By creating an account, you agree to join the {invitation?.projectName} project team.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvitationAcceptance;
